import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ShieldCheck, Ruler, MapPin, ArrowDown } from "lucide-react";
import { IMG } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const ease = [0.22, 1, 0.36, 1];
const LINES = ["Premium Custom", "Curtains, Blinds &", "Expert Installation", "in Melbourne"];
const BADGES = [
  { icon: Ruler, text: "Free In-Home Consultation" },
  { icon: ShieldCheck, text: "100% ACCC Child-Safety Compliant" },
  { icon: MapPin, text: "Local Expert Installation" },
];

const TiltCard = () => {
  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    rx.set(-py * 14); ry.set(px * 14);
  };
  const reset = () => { rx.set(0); ry.set(0); };
  return (
    <motion.div initial={{ opacity: 0, y: 60, rotateX: 12 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ duration: 1.4, delay: 1.1, ease }}
      style={{ perspective: 1200 }} className="relative hidden lg:block" data-testid="hero-tilt-card">
      <motion.div onMouseMove={onMove} onMouseLeave={reset} style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative w-[380px] xl:w-[440px] aspect-[4/5] clip-frame shadow-[0_40px_80px_-30px_rgba(0,0,0,.6)]">
        <img src={IMG.sfold} alt="S-Fold sheer curtains in a Melbourne living room" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
        <div style={{ transform: "translateZ(40px)" }} className="absolute bottom-0 inset-x-0 p-7">
          <p className="eyebrow mb-2">Featured · S-Fold Sheer</p>
          <p className="font-serif text-2xl text-linen">Toorak Residence</p>
          <p className="text-xs text-linen/60 mt-1">Belgian linen · Motorized ceiling track</p>
        </div>
        <div style={{ transform: "translateZ(70px)" }} className="absolute top-6 right-6 h-16 w-16 rounded-full bg-brass text-ink flex items-center justify-center font-mono text-[10px] tracking-widest leading-tight text-center">10 YR<br />WTY</div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-[100svh] bg-ink text-linen overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[10%] h-[120%]">
        <motion.img initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 2.2, ease }}
          src={IMG.hero} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(26,25,23,.92)_0%,rgba(26,25,23,.7)_45%,rgba(26,25,23,.35)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_75%_35%,rgba(197,146,71,.18),transparent_60%)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative container-x pt-36 pb-20 lg:pt-44 lg:pb-28 min-h-[100svh] flex flex-col justify-center">
        <div className="grid lg:grid-cols-[1.25fr_1fr] gap-12 items-center">
          <div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }} className="eyebrow mb-8">Melbourne · Made-to-measure since 2023</motion.p>
            <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl xl:text-[5.2rem]" data-testid="hero-heading">
              {LINES.map((line, i) => (
                <span key={line} className="mask-line">
                  <motion.span className={`block ${i === 3 ? "italic text-brass" : ""}`} initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay: 0.45 + i * 0.13, ease }}>
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease }}
              className="mt-8 max-w-xl text-base md:text-lg text-linen/70 leading-relaxed" data-testid="hero-subheading">
              Transform your space with made-to-measure window furnishings. From precision measuring to flawless fitting, we handle it all.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9, ease }} className="mt-10 flex flex-col sm:flex-row gap-3">
              <button onClick={() => scrollToId("book")} className="btn-brass h-14 px-8" data-testid="hero-book-measure-btn">Book a Free In-Home Measure</button>
              <button onClick={() => scrollToId("estimator")} className="btn-ghost h-14 px-8" data-testid="hero-estimate-btn">Get an Instant Estimate</button>
            </motion.div>
            <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7, duration: 1 }} className="mt-14 flex flex-wrap gap-x-8 gap-y-4" data-testid="hero-trust-badges">
              {BADGES.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-2.5 text-[13px] text-linen/75"><Icon size={16} className="text-brass" />{text}</li>
              ))}
            </motion.ul>
          </div>
          <div className="flex justify-end"><TiltCard /></div>
        </div>
      </motion.div>

      <motion.button onClick={() => scrollToId("products")} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-linen/50 hover:text-brass transition-colors" data-testid="hero-scroll-hint">
        Scroll <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}><ArrowDown size={14} /></motion.span>
      </motion.button>
    </section>
  );
};
